import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Add as AddIcon,
  Campaign as CampaignIcon,
  List as ListIcon,
  AccountBalance as CreditsIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { roundToMaxDecimals } from "../utils/util";
import useApiEp from "../hooks/useApiEp";
import DashStatsCards from "./DashStats";
import DashQuickActions from "./DashQuickActions";

// Mock data for charts
const messageStatsData = [
  { name: "Jan", sent: 4000, delivered: 3800, read: 3200 },
  { name: "Feb", sent: 3000, delivered: 2780, read: 2398 },
  { name: "Mar", sent: 2000, delivered: 1890, read: 1500 },
  { name: "Apr", sent: 2780, delivered: 2390, read: 2100 },
  { name: "May", sent: 1890, delivered: 1790, read: 1580 },
  { name: "Jun", sent: 2390, delivered: 2290, read: 2000 },
];

const campaignTypeData = [
  { name: "Promotional", value: 45 },
  { name: "Transactional", value: 30 },
  { name: "Marketing", value: 25 },
];

const performanceData = [
  { name: "Delivery Rate", value: 95 },
  { name: "Read Rate", value: 84 },
  { name: "Click Rate", value: 12 },
  { name: "Conversion", value: 8 },
];

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

// Quick Actions Component
const QuickActions = () => {
  const navigate = useNavigate();
  const actions = [
    {
      icon: <CampaignIcon />,
      title: "Launch Campaign",
      description: "Start a new RCS campaign",
      color: "#2e7d32",
      link: "/home/campaign",
    },
    {
      icon: <AddIcon />,
      title: "Create Template",
      description: "Design new templates",
      color: "#1976d2",
      link: "/home/create-template",
    },
    {
      icon: <ListIcon />,
      title: "Templates List",
      description: "Templated list view",
      color: "#ed6c02",
      link: "/home/templates",
    },
    {
      icon: <CreditsIcon />,
      title: "Contacts Group",
      description: "Manage your contacts list",
      color: "#9c27b0",
      link: "/home/configurations/contacts",
    },
  ];

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          {actions.map((action, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 3,
                  },
                  borderLeft: `4px solid ${action.color}`,
                }}
                onClick={() => {
                  navigate(action.link);
                }}
              >
                <Box sx={{ color: action.color, mb: 1 }}>{action.icon}</Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {action.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {action.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

// Campaign Performance Chart
const CampaignPerformance = () => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Campaign Performance
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={performanceData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="value" fill="#1976d2" name="Success Rate (%)" />
        </BarChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

// Message Statistics Chart
const MessageStatistics = () => (
  <Card sx={{ mb: 3 }}>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Message Statistics
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={messageStatsData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Line
            type="monotone"
            dataKey="sent"
            stroke="#1976d2"
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="delivered"
            stroke="#2e7d32"
            strokeWidth={2}
          />
          <Line
            type="monotone"
            dataKey="read"
            stroke="#ed6c02"
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

// Campaign Type Distribution
const CampaignTypeDistribution = () => (
  <Card>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Campaign Type Distribution
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={campaignTypeData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) =>
              `${name} ${(percent * 100).toFixed(0)}%`
            }
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {campaignTypeData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

// Recent Activity Component
const RecentActivity = () => {
  const activities = [
    {
      icon: <CampaignIcon />,
      text: 'New campaign "Summer Sale" launched',
      time: "2 hours ago",
    },
    {
      icon: <AddIcon />,
      text: 'Template "Welcome Message" created',
      time: "5 hours ago",
    },
    {
      icon: <CreditsIcon />,
      text: "Credits topped up: 5,000",
      time: "1 day ago",
    },
    {
      icon: <AnalyticsIcon />,
      text: 'Campaign "Spring Promotion" completed',
      time: "2 days ago",
    },
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Recent Activity
        </Typography>
        <List>
          {activities.map((activity, index) => (
            <React.Fragment key={index}>
              <ListItem>
                <ListItemIcon sx={{ color: "primary.main" }}>
                  {activity.icon}
                </ListItemIcon>
                <ListItemText
                  primary={activity.text}
                  secondary={activity.time}
                />
              </ListItem>
              {index < activities.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};

// Main Dashboard Component
const MainDash = () => {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Main Content */}
      <Box sx={{ p: 3 }}>
        {/* Quick Actions */}
        <DashQuickActions />

        {/* Statistics Cards */}
        <DashStatsCards />

        {/* Charts Grid */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <MessageStatistics />
          </Grid>
          <Grid item xs={12} lg={4}>
            <CampaignTypeDistribution />
          </Grid>
          <Grid item xs={12} md={6}>
            <CampaignPerformance />
          </Grid>
          <Grid item xs={12} md={6}>
            <RecentActivity />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default MainDash;
